
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
// Use the same API base as in api.js
const API_BASE = import.meta.env.VITE_API_BASE;

const AuthContext = createContext(null);

const STORAGE_KEY = 'synthloom_auth';

function loadAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveAuth(data) {
  if (data) localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  else localStorage.removeItem(STORAGE_KEY);
}

export function AuthProvider({ children }) {
  const [auth, setAuthRaw] = useState(loadAuth);  // { token, user, workspace }
  const [loading, setLoading] = useState(false);

  const setAuth = useCallback((data) => {
    setAuthRaw(data);
    saveAuth(data);
  }, []);

  /* ── helper: build headers with JWT ── */
  const authHeaders = useCallback(() => {
    if (!auth?.token) return {};
    return { Authorization: `Bearer ${auth.token}` };
  }, [auth]);

  /* ── Login ── */
  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(err.detail || 'Login failed');
      }
      const data = await res.json();
      setAuth({ token: data.token, user: data.user, workspace: data.workspace });
      return data;
    } finally { setLoading(false); }
  }, [setAuth]);

  /* ── Register ── */
  const register = useCallback(async (username, displayName, email, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, display_name: displayName, name: displayName, email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Registration failed' }));
        throw new Error(err.detail || 'Registration failed');
      }
      const data = await res.json();
      if (data.pending) {
        return data; // account awaiting admin approval — don't log in yet
      }
      setAuth({ token: data.token, user: data.user, workspace: data.workspace });
      return data;
    } finally { setLoading(false); }
  }, [setAuth]);

  /* ── Logout ── */
  const logout = useCallback(() => { setAuth(null); }, [setAuth]);

  /* ── Workspaces ── */
  const listWorkspaces = useCallback(async () => {
    const res = await fetch(`${API_BASE}/auth/workspaces`, { headers: authHeaders() });
    if (res.status === 401) {
      setAuth(null); // token expired or invalid — clear session
      throw new Error('SESSION_EXPIRED');
    }
    if (!res.ok) throw new Error('Could not load workspaces');
    const data = await res.json();
    return data.workspaces || [];
  }, [authHeaders, setAuth]);

  const createWorkspace = useCallback(async (name) => {
    const res = await fetch(`${API_BASE}/auth/workspaces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed' }));
      throw new Error(err.detail || 'Create failed');
    }
    return res.json();
  }, [authHeaders]);

  const switchWorkspace = useCallback(async (workspaceId) => {
    const res = await fetch(`${API_BASE}/auth/workspaces/switch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    if (!res.ok) throw new Error('Switch failed');
    const data = await res.json();
    setAuth((prev) => ({ ...prev, token: data.token, workspace: data.workspace }));
    return data;
  }, [authHeaders, setAuth]);

  const deleteWorkspace = useCallback(async (workspaceId) => {
    const res = await fetch(`${API_BASE}/auth/workspaces/${workspaceId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Delete failed');
    return res.json();
  }, [authHeaders]);

  const renameWorkspace = useCallback(async (workspaceId, newName) => {
    const res = await fetch(`${API_BASE}/auth/workspaces/${workspaceId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name: newName }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Rename failed' }));
      throw new Error(err.detail || 'Rename failed');
    }
    const data = await res.json();
    // Update current workspace name if it's the active one
    setAuth((prev) => {
      if (prev?.workspace?.id === workspaceId) {
        return { ...prev, workspace: { ...prev.workspace, name: data.name } };
      }
      return prev;
    });
    return data;
  }, [authHeaders, setAuth]);

  /* ── Bootstrap admin (first-user or self-promote) ── */
  const makeAdmin = useCallback(async () => {
    const res = await fetch(`${API_BASE}/auth/admin/make-admin`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed' }));
      throw new Error(err.detail || 'Failed to become admin');
    }
    const data = await res.json();
    // Update token and user with admin flag
    setAuth((prev) => ({
      ...prev,
      token: data.token,
      user: { ...prev.user, is_admin: true },
    }));
    return data;
  }, [authHeaders, setAuth]);

  /* ── Update profile (display_name, avatar_url) ── */
  const updateProfile = useCallback(async ({ display_name, avatar_url }) => {
    const body = {};
    if (display_name !== undefined) body.display_name = display_name;
    if (avatar_url !== undefined) body.avatar_url = avatar_url;
    const res = await fetch(`${API_BASE}/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed' }));
      throw new Error(err.detail || 'Profile update failed');
    }
    const data = await res.json();
    setAuth((prev) => ({
      ...prev,
      token: data.token,
      user: { ...prev.user, ...data.user },
    }));
    return data;
  }, [authHeaders, setAuth]);

  /* ── Scoped fetch helper — auto-attaches JWT ── */
  const apiFetch = useCallback(async (url, opts = {}) => {
    const merged = {
      ...opts,
      headers: { ...authHeaders(), ...(opts.headers || {}) },
    };
    const res = await fetch(url, merged);
    if (res.status === 401) {
      setAuth(null); // token expired or invalid — force re-login
    }
    return res;
  }, [authHeaders, setAuth]);

  const value = {
    auth,
    user: auth?.user || null,
    workspace: auth?.workspace || null,
    token: auth?.token || null,
    isAuthenticated: !!auth?.token,
    loading,
    login,
    register,
    logout,
    listWorkspaces,
    createWorkspace,
    switchWorkspace,
    deleteWorkspace,
    renameWorkspace,
    makeAdmin,
    updateProfile,
    apiFetch,
    authHeaders,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
